import type { FC } from "react";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { prepareSeoData } from "~/core/transformers/seo.transformer";
import {
  featureFlagsContext,
  sdkContext,
  settingsContext,
} from "~/server/get-load-context";
import {
  createLcpImageLinkHeaders,
  resolveLcpImage,
} from "~/server/resolve-lcp-image.server";
import { STATIC_PAGE_CACHE_HEADERS } from "~/shared/cache-headers";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPagesByConstructor } from "~/widgets/pages-by-constructor/page-by-constructor.server";

import type { Route } from "./+types/$lang._index";

const createHomeValidator = (): ObjectSchema<{
  lang: string;
}> => {
  return object({
    lang: string().required(),
  });
};

export const loader = (async ({ context, params, request }) => {
  const settings = context.get(settingsContext);
  const sdk = context.get(sdkContext);
  const featureFlags = context.get(featureFlagsContext);

  const { lang } = createHomeValidator().validateSync(params);
  const homeSlug =
    typeof settings?.app?.home?.slug === "string"
      ? settings.app.home.slug
      : undefined;
  const isHomeConstructorPageEnabled = featureFlags.getItem(
    "homePageConstructor",
  ).enabled;

  if (isHomeConstructorPageEnabled && !homeSlug) {
    throw new Response(
      JSON.stringify({
        message: "Home page not found",
      }),
      {
        status: 404,
        statusText: "Not Found",
      },
    );
  }

  const pagesConstructorData = homeSlug
    ? await loadPagesByConstructor(sdk, homeSlug, lang, {
        slug: homeSlug ?? "",
        segment: settings?.app?.home?.segment,
        pageList: "whenRelatedPages",
      })
    : undefined;

  const url = new URL(request.url);
  const cmsPath = `${url.protocol}//${url.host}`;
  const lcpImage = resolveLcpImage(pagesConstructorData?.page, cmsPath);
  const responseHeaders = new Headers(STATIC_PAGE_CACHE_HEADERS);

  for (const linkHeader of createLcpImageLinkHeaders(lcpImage)) {
    responseHeaders.append("Link", linkHeader);
  }

  return responseData(
    {
      pageData: pagesConstructorData?.page,
      pages: pagesConstructorData?.pages,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
      lcpImage,
    },
    {
      headers: responseHeaders,
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({
  data,
  params,
  matches,
}) => {
  return prepareSeoData({
    pageSeo: data?.pageData?.page.seo,
    fallbackSeo: data?.pageData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
    matches,
  });
};

const Page: FC<Route.ComponentProps> = function ({
  loaderData: { pageData, pages },
}) {
  if (!pageData) {
    return null;
  }

  return <PageConstructor data={pageData} pages={pages} />;
};

export default Page;
